/**
 * Speech Recognition Hook
 *
 * Provides voice-to-text functionality using the Web Speech API.
 * Works in Chrome, Edge, and Safari. Falls back gracefully in unsupported browsers.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// Web Speech API types (not included in standard TypeScript lib)
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export interface UseSpeechRecognitionOptions {
  /** Language for recognition (default: 'es-AR') */
  lang?: string;
  /** Show interim results while speaking (default: true) */
  interimResults?: boolean;
  /** Callback when transcription is received */
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
}

export interface UseSpeechRecognitionResult {
  /** Whether speech recognition is supported in this browser */
  isSupported: boolean;
  /** Whether currently listening */
  isListening: boolean;
  /** Current interim transcript (while speaking) */
  transcript: string;
  /** Start listening */
  startListening: () => void;
  /** Stop listening */
  stopListening: () => void;
  /** Toggle listening state */
  toggleListening: () => void;
}

/**
 * Hook for speech recognition using Web Speech API
 */
export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {}
): UseSpeechRecognitionResult {
  const {
    lang = 'es-AR',
    interimResults = true,
    onTranscript,
    onError,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Check browser support
  const isSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize recognition instance
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionClass();

    recognition.continuous = true; // Keep listening until user stops
    recognition.interimResults = interimResults;
    recognition.lang = lang;

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript('');
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      // Update interim display
      if (interimTranscript) {
        setTranscript(interimTranscript);
        onTranscript?.(interimTranscript, false);
      }

      // Send final result
      if (finalTranscript) {
        setTranscript(finalTranscript);
        onTranscript?.(finalTranscript, true);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsListening(false);

      // Map error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        'not-allowed': 'Permiso de micrófono denegado. Habilitá el micrófono en la configuración del navegador.',
        'no-speech': 'No se detectó voz. Volvé a intentar.',
        'audio-capture': 'No se encontró micrófono. Verificá que esté conectado.',
        'network': 'Error de red. Verificá tu conexión a internet.',
        'aborted': '', // User cancelled, no error message needed
      };

      const message = errorMessages[event.error] || `Error de reconocimiento de voz: ${event.error}`;
      if (message) {
        onError?.(message);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.abort();
    };
  }, [isSupported, lang, interimResults, onTranscript, onError]);

  const startListening = useCallback(() => {
    if (!isSupported || !recognitionRef.current || isListening) return;

    try {
      recognitionRef.current.start();
    } catch (error) {
      // Recognition might already be started
      console.warn('[SpeechRecognition] Failed to start:', error);
    }
  }, [isSupported, isListening]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) return;

    try {
      recognitionRef.current.stop();
    } catch (error) {
      console.warn('[SpeechRecognition] Failed to stop:', error);
    }
  }, [isListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isSupported,
    isListening,
    transcript,
    startListening,
    stopListening,
    toggleListening,
  };
}

export default useSpeechRecognition;
